/* ══════════════════════════════════════════════════════════════
   KANA DRAW — recognition

   No model and no training data. The drawing is compared against every
   kana of the same script, rendered from the font the app already
   loads, and the closest match wins.

   Asking "which of these 46 shapes is it most like?" is a far better
   question than "is this a か?". The second needs an absolute threshold,
   which has to be tuned per character and is wrong for anyone whose
   handwriting is heavier or lighter than the one it was tuned on. A
   ranking needs no threshold at all — it only needs the right answer to
   beat the other 45, and it produces something genuinely useful when it
   fails: the character you actually drew.

   The pipeline, applied identically to the drawing and to every
   template so the comparison is like for like:

     rasterise → crop to the ink → scale to a fixed box, aspect kept
              → blur → cosine similarity

   Aspect ratio is preserved deliberately. し is tall and narrow, つ is
   wide and flat; squashing everything into a square throws away one of
   the strongest signals there is.

   The blur is what absorbs the difference between a 7px pen stroke and
   a font's tapered one, and a few pixels of wobble with it. Too little
   and nothing matches; too much and everything does. The value below
   was measured, not guessed — see the accuracy figures in the review.
══════════════════════════════════════════════════════════════ */

const RASTER = 256;   // working canvas for the initial rasterise
const GRID = 64;      // comparison resolution
const FIT = 48;       // the glyph is scaled to fit this, centred in GRID
const BLUR = 3;       // box-blur radius, in GRID pixels
const INK = 0.15;     // alpha above which a pixel counts as ink
const FONT = '"Noto Sans JP", sans-serif';
/* Stroke width as a fraction of the character's own size, chosen to sit
   near the weight of the font the templates are rendered from. Measured
   rather than guessed — see the size-invariance figures in review. */
const PEN_RATIO = 0.10;

let scratch = null;
const templates = new Map();   // `${weight}:${char}` -> Float32Array

function ctxOf(size) {
  if (!scratch) scratch = document.createElement('canvas');
  scratch.width = size;
  scratch.height = size;
  const ctx = scratch.getContext('2d', { willReadFrequently: true });
  ctx.clearRect(0, 0, size, size);
  return ctx;
}

function alphaOf(ctx, size) {
  const d = ctx.getImageData(0, 0, size, size).data;
  const out = new Float32Array(size * size);
  for (let i = 0, p = 0; i < d.length; i += 4, p += 1) out[p] = d[i + 3] / 255;
  return out;
}

function sample(src, size, x, y) {
  const x0 = Math.floor(x); const y0 = Math.floor(y);
  const x1 = Math.min(size - 1, x0 + 1); const y1 = Math.min(size - 1, y0 + 1);
  if (x0 < 0 || y0 < 0 || x0 >= size || y0 >= size) return 0;
  const fx = x - x0; const fy = y - y0;
  const a = src[y0 * size + x0]; const b = src[y0 * size + x1];
  const c = src[y1 * size + x0]; const d = src[y1 * size + x1];
  return a * (1 - fx) * (1 - fy) + b * fx * (1 - fy) + c * (1 - fx) * fy + d * fx * fy;
}

/* Crop to the ink, scale it to FIT with the aspect kept, centre it. This
   is what makes where and how large you drew irrelevant. */
function normalise(src, size) {
  let minX = size; let minY = size; let maxX = -1; let maxY = -1;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (src[y * size + x] > INK) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;   // nothing drawn

  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const scale = FIT / Math.max(w, h);
  const offX = (GRID - w * scale) / 2;
  const offY = (GRID - h * scale) / 2;

  const out = new Float32Array(GRID * GRID);
  for (let oy = 0; oy < GRID; oy += 1) {
    for (let ox = 0; ox < GRID; ox += 1) {
      const sx = (ox - offX) / scale + minX;
      const sy = (oy - offY) / scale + minY;
      out[oy * GRID + ox] = sample(src, size, sx, sy);
    }
  }
  return out;
}

/* Separable box blur, two passes — close enough to a Gaussian for this
   and a great deal cheaper. */
function blur(vec, radius) {
  const pass = (input) => {
    const out = new Float32Array(GRID * GRID);
    for (let y = 0; y < GRID; y += 1) {
      for (let x = 0; x < GRID; x += 1) {
        let sum = 0; let n = 0;
        for (let k = -radius; k <= radius; k += 1) {
          const xx = x + k;
          if (xx >= 0 && xx < GRID) { sum += input[y * GRID + xx]; n += 1; }
        }
        out[y * GRID + x] = sum / n;
      }
    }
    const out2 = new Float32Array(GRID * GRID);
    for (let x = 0; x < GRID; x += 1) {
      for (let y = 0; y < GRID; y += 1) {
        let sum = 0; let n = 0;
        for (let k = -radius; k <= radius; k += 1) {
          const yy = y + k;
          if (yy >= 0 && yy < GRID) { sum += out[yy * GRID + x]; n += 1; }
        }
        out2[y * GRID + x] = sum / n;
      }
    }
    return out2;
  };
  return pass(pass(vec));
}

function cosine(a, b) {
  let dot = 0; let na = 0; let nb = 0;
  for (let i = 0; i < a.length; i += 1) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  return (na && nb) ? dot / Math.sqrt(na * nb) : 0;
}

const prepare = (raw, size) => {
  const n = normalise(raw, size);
  return n ? blur(n, BLUR) : null;
};

/* A template per character, built once. The font must actually be
   loaded first — rasterising before it arrives silently produces
   templates in the fallback font, and every comparison is then made
   against the wrong shapes. */
export async function ensureFont(weight = 700) {
  if (document.fonts?.load) {
    try { await document.fonts.load(`${weight} 48px ${FONT}`); } catch { /* fall through */ }
  }
}

function templateFor(char, weight = 700) {
  const key = `${weight}:${char}`;
  const hit = templates.get(key);
  if (hit) return hit;

  const ctx = ctxOf(RASTER);
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${weight} ${Math.round(RASTER * 0.68)}px ${FONT}`;
  ctx.fillText(char, RASTER / 2, RASTER / 2);

  const built = prepare(alphaOf(ctx, RASTER), RASTER);
  templates.set(key, built);
  return built;
}

/* Paint the strokes at RASTER scale so the result does not depend on how
   large the pad happened to be. */
/* The extent of the drawing itself, in pad pixels. */
function strokeSpan(strokes) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  strokes.forEach(pts => (pts || []).forEach(pt => {
    if (pt.x < minX) minX = pt.x;
    if (pt.x > maxX) maxX = pt.x;
    if (pt.y < minY) minY = pt.y;
    if (pt.y > maxY) maxY = pt.y;
  }));
  if (!Number.isFinite(minX)) return 0;
  return Math.max(maxX - minX, maxY - minY);
}

export function rasteriseStrokes(strokes, padSize, lineWidth) {
  const ctx = ctxOf(RASTER);
  const k = RASTER / padSize;

  /* Ink the strokes at a width proportional to how large the character was
     drawn, not to the pad.

     This is what makes size genuinely irrelevant. Normalise() scales the
     drawing's bounding box up to a constant FIT, so a fixed pen scales
     inversely with it: draw small and the strokes end up two or three
     times thicker, relative to the character, than if you had drawn
     large. The templates have one fixed weight, so small drawings were
     systematically penalised — a correct character marked wrong purely
     for being little.

     Scaling the pen with the drawing cancels that exactly: thickness
     after normalisation is PEN_RATIO * FIT whatever size you drew.

     Note this is only the width used for *comparison*. What you see on
     the pad is still a constant-width pen, which is what a pen should
     look like. */
  const span = strokeSpan(strokes);
  const pen = span > 0 ? span * PEN_RATIO : lineWidth;

  ctx.strokeStyle = '#fff';
  ctx.lineWidth = Math.max(2, pen * k);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  strokes.forEach(pts => {
    if (!pts || !pts.length) return;
    if (pts.length === 1) {
      ctx.beginPath();
      ctx.arc(pts[0].x * k, pts[0].y * k, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(pts[0].x * k, pts[0].y * k);
    for (let i = 1; i < pts.length - 1; i += 1) {
      const mx = (pts[i].x + pts[i + 1].x) / 2 * k;
      const my = (pts[i].y + pts[i + 1].y) / 2 * k;
      ctx.quadraticCurveTo(pts[i].x * k, pts[i].y * k, mx, my);
    }
    ctx.lineTo(pts[pts.length - 1].x * k, pts[pts.length - 1].y * k);
    ctx.stroke();
  });
  return alphaOf(ctx, RASTER);
}

/* Score the drawing against every candidate, best first.
   `candidates` is the full kana set for the script — not just the ones in
   this session — so "that looked more like ツ" can name a character the
   session never contained, which is exactly when the learner most needs
   telling. */
export function rank(strokes, padSize, lineWidth, candidates) {
  const drawn = prepare(rasteriseStrokes(strokes, padSize, lineWidth), RASTER);
  if (!drawn) return [];
  return candidates
    .map(c => ({ kana: c.kana, romaji: c.romaji, score: cosine(drawn, templateFor(c.kana)) }))
    .sort((a, b) => b.score - a.score);
}

/* The verdict. Correct when the target wins outright, and also when it is
   close enough behind the winner to be a coin toss — シ against ツ differs
   by a couple of stroke angles, and a blurred bitmap cannot always call
   that. Being generous there is the right way to be wrong: a learner told
   they failed a character they actually drew will stop trusting the
   grader, while an occasional undeserved pass costs almost nothing. */
/* How far behind the winner the right answer may sit and still count.

   Relative, not absolute. Neat handwriting produces scores around 0.85
   and hurried handwriting around 0.6, and a fixed gap of 0.02 means
   something quite different at each end. A percentage of the winning
   score means the same thing everywhere.

   There was an absolute confidence gate here too, and it was a mistake:
   it demanded the winner clear 0.72 before the verdict counted, which
   real handwriting rarely does, so the grader abstained on nearly every
   drawing and handed the decision back to the learner — the exact thing
   auto-marking was meant to remove. It was also the same absolute
   threshold this file's own header argues against. A ranking does not
   need one: the right answer only has to beat the other forty-five.

   Being generous at the boundary is the right way to be wrong. An
   occasional undeserved pass costs almost nothing; failing someone who
   drew the character correctly is how a grader loses their trust. */
export const MARGIN = 0.04;   // fraction of the winning score

export function judge(strokes, padSize, lineWidth, target, candidates) {
  const ranked = rank(strokes, padSize, lineWidth, candidates);
  if (!ranked.length) return { empty: true, correct: false, ranked: [] };

  const best = ranked[0];
  const mine = ranked.find(r => r.kana === target) || { score: 0 };
  const correct = best.kana === target
    || (best.score - mine.score) <= best.score * MARGIN;

  return {
    empty: false,
    correct,
    best,
    mine,
    /* Only worth naming when it is not the answer and it actually won. */
    confusedWith: correct ? null : best,
    ranked: ranked.slice(0, 3),
  };
}
